<template>
    <div>
        <template v-if="hardError">
            <b-alert variant="danger" :show="true" class="mb-2">Sorry, an unknown error has occured. This sometimes happens if we sell out. Please refresh the page.</b-alert>
        </template>
        <template v-else>

            <b-alert variant="danger" :show="hasError" class="mb-2">{{ error }}</b-alert>

            <template v-if="isFree">
                <b-form-checkbox
                    v-model="tandc"
                    name="tandc"
                    value="accepted"
                    unchecked-value="not_accepted"
                    class="my-3">
                    By registering on this course I consent to Virtuoso safety centre <a href="https://playvirtuoso.com/tandc/" target="_blank" class="text-underline">terms and conditions</a>.
                </b-form-checkbox>

                <b-button variant="success" :disabled="isBusy" @click="handleEnrol">Enrol Now - FREE</b-button>
            </template>
            <template v-else>
                <template v-if="seat == 'active'">
                    <p>You are paying for an interactive place in the Virtuoso Stage.</p>
                    <p>
                        To make courses the best for everyone, we ask you:
                        <ul>
                            <li>Keep your camera on at all times</li>
                            <li>Participate in the session</li>
                            <li>Join on a laptop and use Google Chrome.</li>
                        </ul>
                    </p>
                    <p>
                        If you agree to the above, please proceed to pay and we look forward to seeing you for this session.
                    </p>
                </template>

                <div class="text-center">
                    <template v-if="paymentMethod">
                        <div class="my-3">

                            <div class="mb-1">
                                <template v-if="amountToPay == 0">FREE</template>
                                <template v-else>
                                    £{{ amountToPay }}
                                </template>
                            </div>

                            <div class="d-inline-block text-center">
                                <div class="payment-btn-wrapper">
                                    <span class="text-monospace ml-3">
                                        <span class="mr-3">{{ paymentMethod.card_type.charAt(0).toUpperCase() + paymentMethod.card_type.slice(1) }}</span>
                                        **** **** **** {{ paymentMethod.last4 }}
                                    </span>
                                </div>

                                <div class="mb-3 mt-1">
                                    <b-button variant="link" @click="isShowUpdatePaymentMethod = true" v-if="!isShowUpdatePaymentMethod">Update payment method</b-button>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <b-button variant="link" @click="isShowUpdatePaymentMethod = true" v-if="!isShowUpdatePaymentMethod">Update payment method</b-button>
                    </template>
                    <card-update-component
                            v-if="isShowUpdatePaymentMethod"
                            :user="user"
                            :is-inline="true"></card-update-component>

                    <hr>

                    <div class="row justify-content-center">
                        <div class="col-md-6 text-center">
                            <div class="form-group">
                                <label for="discountCode">Have a discount code?  Enter it below.</label>
                                <div class="input-group">
                                    <input type="text" class="form-control" id="discountCode" v-model="discountCode">
                                    <div class="input-group-append">
                                        <b-button variant="primary" class="px-3" @click="handleDiscount">Apply</b-button>
                                    </div>
                                </div>
                                <b-alert variant="success" :show="discountSuccessMsg.length > 0">{{ discountSuccessMsg }}</b-alert>
                            </div>
                        </div>
                    </div>

                    <template v-if="paymentMethod">
                        <b-form-checkbox
                            v-model="tandc"
                            name="tandc"
                            value="accepted"
                            unchecked-value="not_accepted"
                            class="my-3">
                            By purchasing this course I consent to Virtuoso safety centre <a href="https://playvirtuoso.com/tandc/" target="_blank" class="text-underline">terms and conditions</a>.
                        </b-form-checkbox>

                        <b-button variant="success" :disabled="isBusy" @click="handleEnrol">Buy Now</b-button>
                    </template>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import { globalEventFire } from '../tracker.js';

    export default {
        props: {
            course: {
                type: Object,
                required: true,
            },
            dateset: {
                type: Object,
                required: true,
            },
            seat: {
                type: String,
                required: true,
            },
        },

        data() {
            return {
                stripeAPIToken: document.querySelector('meta[name="stripe-key"]').getAttribute('content'),
                isBusy: false,
                hardError: false,
                hasError: false,
                error: '',
                currentUrl: '',
                tandc: 'not_accepted',
                isShowUpdatePaymentMethod: false,
                paymentMethod: null,
                isFree: false,
                isAuth: false,
                isSubscribed: false,
                discountCode: '',
                enteredDiscountCode: '',
                discountSuccessMsg: '',
                discountPercentage: 0,
                discountAmount: 0,
                priceAfterDiscount: 0,
                appliedDiscountCodeId: null,
                isFreeDiscountCode: false,
            }
        },

        computed: {
            // Amount to pay
            amountToPay: function() {
                let amt = 0;

                // If we have a priceAfterDiscount figure then use that, as it's been calculated server-side.
                // Otherwise, calculate.
                if(this.priceAfterDiscount > 0) {
                    amt = this.priceAfterDiscount;
                } else {
                    if(this.isFreeDiscountCode) {
                        amt = 0;
                    } else {
                        if(this.isSubscribed) {
                            if(this.seat == 'active') {
                                if(!this.course.is_active_free) {
                                    amt = this.dateset.priceActiveUnformatted;
                                }
                            } else if(this.seat == 'observer') {
                                if(!this.course.is_observer_free) {
                                    amt = this.dateset.priceObserverUnformatted;
                                }
                            }
                        } else {
                            if(this.seat == 'active') {
                                if(!this.course.is_active_free_non_subs) {
                                    amt = this.dateset.priceActiveNonSubsUnformatted;
                                }
                            } else if(this.seat == 'observer') {
                                if(!this.course.is_observer_free_non_subs) {
                                    amt = this.dateset.priceObserverNonSubsUnformatted;
                                }
                            }
                        }
                    }
                }

                amt = parseFloat(Math.round(amt * 100) / 100).toFixed(2);

                return amt;
            },
        },

        mounted() {
            this.currentUrl = window.location.pathname;

            // User logged in?
            if(window.virtuoso) {
                if(window.virtuoso.is_auth) {
                    this.isAuth = true;
                    this.isSubscribed = window.virtuoso.is_subscribed;
                    this.user = window.virtuoso.user;
                }
            }

            // We check server side so it's safe to do whatever here
            if(this.isSubscribed) {
                if(this.seat == 'active') {
                    this.isFree = this.dateset.is_active_free;
                } else if(this.seat == 'observer') {
                    this.isFree = this.dateset.is_observer_free;
                } else {
                    this.isFree = false;
                    this.hardError = true;
                }
            } else {
                if(this.seat == 'active') {
                    this.isFree = this.dateset.is_active_free_non_subs;
                } else if(this.seat == 'observer') {
                    this.isFree = this.dateset.is_observer_free_non_subs;
                } else {
                    this.isFree = false;
                    this.hardError = true;
                }
            }

            // If it's a free course, we don't need to do anything else
            if(this.dateset.is_free) {
                this.isFree = true;
            }

            // Load stripe
            this.includeScript('js.stripe.com/v3/', function(){
                this.configureStripe();
            }.bind(this) );

            this.loadPaymentMethod();

            // When payment methods have been updated refresh that section
            Bus.$on("updatedPaymentMethods", (d) => {
                this.loadPaymentMethod();
                this.isShowUpdatePaymentMethod = false;
            });
        },

        methods: {
            includeScript( URL, callback ){
                let documentTag = document, tag = 'script',
                    object = documentTag.createElement(tag),
                    scriptTag = documentTag.getElementsByTagName(tag)[0];
                object.src = '//' + URL;
                if (callback) { object.addEventListener('load', function (e) { callback(null, e); }, false); }
                scriptTag.parentNode.insertBefore(object, scriptTag);
            },

            /*
                Configures Stripe by setting up the elements and
                creating the card element.
            */
            configureStripe() {
                this.stripe = Stripe( this.stripeAPIToken );
            },

            // Handle the discount - called when a user clicks/enters a discount code
            handleDiscount() {
                this.isBusy = true;
                axios.post('/api/discounts/course/' + this.course.id + '/check?seat=' + this.seat + '&dateset=' + this.dateset.id, {
                    'code': this.discountCode
                }).then((response) => {
                        if(response.data.success) {
                            this.isBusy = false;
                            this.hasError = false;
                            this.error = '';
                            this.discountPercentage = response.data.percentage;
                            this.discountSuccessMsg = 'A ' + this.discountPercentage + '% discount has been applied to your order.';
                            this.discountAmount = response.data.discountAmount;
                            this.priceAfterDiscount = response.data.priceAfterDiscount;
                            this.appliedDiscountCodeId = response.data.id;
                            this.enteredDiscountCode = this.discountCode;

                            if(this.discountPercentage == 100) {
                                this.isFreeDiscountCode = true;
                            } else {
                                this.isFreeDiscountCode = false;
                            }
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                            this.discountPercentage = 0;
                            this.discountSuccessMsg = '';
                            this.discountAmount = 0;
                            this.priceAfterDiscount = 0;
                            this.appliedDiscountCodeId = null;
                            this.isFreeDiscountCode = false;
                        }
                    })
                    .catch((error) => {
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            },

            //
            loadPaymentMethod() {
                // Load payment methods
                this.isBusy = true;
                this.isLoadingPaymentMethods = true;
                axios.get('/api/me/payment_method')
                    .then((response) => {
                        this.paymentMethod = response.data;
                        this.isBusy = false;
                    });
            },

            // Handle enrolment
            handleEnrol() {
                if(this.tandc == 'not_accepted') {
                    this.hasError = true;
                    this.error = 'You must accept the terms and conditions to continue.';
                } else {
                    this.hasError = false;
                    this.error = '';

                    this.isBusy = true;

                    if(this.isFree || this.isFreeDiscountCode) {
                        console.log('fireEnrolment');
                        this.fireEnrolment();
                    } else {
                        console.log('handlePayment');
                        this.handlePayment();
                    }
                }
            },

            // Actually enrol them
            fireEnrolment() {
                // Fire it
                let newUserAppend = this.isNewUser ? '?newUser=true&enrolled=true' : '?enrolled=true';
                axios.post('/api/course/' + this.course.id + '/' + this.dateset.id + '/enrol/' + this.seat + (this.appliedDiscountCodeId ? '?discountCodeId=' + this.appliedDiscountCodeId : ''))
                    .then((response) => {
                        if(response.data.success) {
                            // Fire analytics event
                            this.fireAnalytics(response.data.enrolment_id);
                            location.href = this.course.uri + '/enrol/' + this.seat + '/success' + newUserAppend;
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                        }
                    })
                    .catch((error) => {
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            },

            // Attempt payment and then enrol
            handlePayment() {
                axios.post('/api/course/' + this.course.id + '/' + this.dateset.id + '/pay/' + this.seat + (this.appliedDiscountCodeId ? '?discountCodeId=' + this.appliedDiscountCodeId : ''))
                    .then((response) => {
                        if(response.data.success) {
                            this.successfulPaymentRedirect(response.data.metadata.enrolment_id ?? 0);
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                        }

                        if(response.data.status) {
                            let payment_intent = response.data;

                            if (payment_intent) {
                                const { client_secret, status } = payment_intent;
                                if (status === 'requires_action') {
                                    this.stripe.confirmCardPayment(client_secret).then((result) => {
                                        if (result.error) {
                                            // Display error message in your UI.
                                            // The card was declined (i.e. insufficient funds, card has expired, etc)
                                            this.hasError = true;
                                            this.error = result.error.message;
                                            this.isBusy = false;
                                        } else {
                                            // Redirect after a timeout to give the webhook time to run
                                            this.successfulPaymentRedirect(response.data.metadata.enrolment_id ?? 0);
                                        }
                                    });
                                } else if(status === 'succeeded') {
                                    this.successfulPaymentRedirect(response.data.metadata.enrolment_id ?? 0);
                                } else {
                                    this.hasError = true;
                                    this.error = 'An error was encountered.  Error code #FAS2135';
                                }
                            }
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            },

            // When payment is successful redirect after firing analytics
            successfulPaymentRedirect(enrolment_id) {
                let newUserAppend = this.isNewUser ? '&newUser=true&enrolled=true' : '&enrolled=true';

                // Fire success
                this.fireAnalytics(enrolment_id);

                setTimeout(() => {
                    location.href = this.course.uri + '/enrol/' + this.seat + '/success?paid' + newUserAppend;
                }, 1500);
            },

            // Fire analytics
            fireAnalytics(enrolment_id) {
                globalEventFire('Purchase', 'LC', {
                    'item_id': this.course.id,
                    'category': 'Live Course',
                    'name': this.seat +' on '+ this.course.name,
                    'num_items': 1,
                    'amount': this.amountToPay,
                    'enrolment_id': enrolment_id,
                });
            }
        }
    }
</script>
